import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Bell, Check } from "lucide-react";

function timeAgo(iso) {
  try {
    const d = new Date(iso);
    const diff = Math.max(0, Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const reloadRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get("/notifications", { params: { limit: 20 } });
        if (cancelled) return;
        setItems(res.data?.items || []);
        setUnread(res.data?.unread_count || 0);
      } catch { /* silent */ }
    };
    reloadRef.current = load;
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const markRead = async (notifId) => {
    try {
      await api.patch(`/notifications/${notifId}/read`);
      reloadRef.current && reloadRef.current();
    } catch { /* ignore */ }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      reloadRef.current && reloadRef.current();
    } catch { /* ignore */ }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell" aria-label="notifications">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <Badge
              variant="default"
              className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 text-[10px] rounded-full"
              data-testid="notification-unread-count"
            >
              {unread > 9 ? "9+" : unread}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unread > 0 && (
            <Button variant="ghost" size="sm" className="h-auto py-0 px-2 text-xs" onClick={markAll} data-testid="notification-mark-all">
              <Check className="w-3 h-3 mr-1" /> Mark all read
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground">No notifications</div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => !n.read && markRead(n.id)}
                className={`w-full text-left px-3 py-2 border-b last:border-b-0 border-border hover:bg-muted/40 flex flex-col gap-0.5 ${n.read ? "" : "bg-blue-50/60"}`}
                data-testid={`notification-${n.id}`}
              >
                <div className="text-xs leading-snug">{n.message}</div>
                <div className="text-[10px] text-muted-foreground">{timeAgo(n.created_at)}</div>
              </button>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
